import { useState } from 'react'
import { AlertTriangle, Check, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import type { AgentConfirmation } from '@/lib/api/agent'
import { cn } from '@/lib/utils'

interface Props {
  confirmation: AgentConfirmation
  onConfirm: (toolCallId: string, secondConfirmation: boolean) => Promise<void>
  onReject: (toolCallId: string) => Promise<void>
}

function readableTarget(targetType: string): string {
  return targetType
    .toLowerCase()
    .split('_')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')
}

/** Renders the proposed values so the operator approves specifics, not a summary. */
function DiffRows({ diff }: { diff: Record<string, unknown> | null }) {
  const after = (diff?.after ?? diff) as Record<string, unknown> | null
  if (!after || typeof after !== 'object') return null

  const rows = Object.entries(after).filter(
    ([, value]) => value !== null && value !== undefined && value !== '',
  )
  if (rows.length === 0) return null

  return (
    <dl className="mt-3 grid grid-cols-[minmax(0,9rem)_1fr] gap-x-3 gap-y-1.5 text-sm">
      {rows.map(([key, value]) => (
        <div key={key} className="contents">
          <dt className="truncate text-muted-foreground">{key.replace(/_/g, ' ')}</dt>
          <dd className="break-words text-foreground">
            {typeof value === 'object' ? JSON.stringify(value) : String(value)}
          </dd>
        </div>
      ))}
    </dl>
  )
}

/**
 * A pending write, shown before anything happens.
 *
 * Nothing has been written when this renders. The operator is approving the
 * values in front of them, which is why the diff comes from the server rather
 * than being recomputed here.
 */
export function ConfirmationCard({ confirmation, onConfirm, onReject }: Props) {
  const [busy, setBusy] = useState(false)
  const [acknowledged, setAcknowledged] = useState(false)
  const resolved = Boolean(confirmation.confirmed_at || confirmation.rejected_at)
  const needsSecond = confirmation.requires_second_confirmation

  const run = async (action: () => Promise<void>) => {
    setBusy(true)
    try {
      await action()
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      data-awaiting-approval={resolved ? undefined : ''}
      tabIndex={-1}
      // Amber only while it waits on you; a decided card goes back to neutral.
      className={cn(
        'scroll-mt-3 rounded-xl border bg-card p-4 outline-none',
        !resolved && 'border-waiting/50',
      )}
    >
      <div className="flex items-start gap-2">
        <AlertTriangle
          className={cn(
            'mt-0.5 size-4 shrink-0',
            resolved ? 'text-muted-foreground' : 'text-waiting',
          )}
        />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant="outline" className="text-xs">
              {readableTarget(confirmation.target_type)}
            </Badge>
            {needsSecond && (
              <Badge variant="accent-soft" className="text-xs">
                Leaves Helm
              </Badge>
            )}
          </div>
          <p className="mt-2 text-sm text-foreground">{confirmation.summary}</p>

          <DiffRows diff={confirmation.diff} />

          {confirmation.external_effects.length > 0 && (
            <ul className="mt-3 space-y-1 text-sm text-waiting">
              {confirmation.external_effects.map((effect, index) => (
                <li key={index}>· {String(effect.description ?? JSON.stringify(effect))}</li>
              ))}
            </ul>
          )}

          {resolved ? (
            <p className="mt-3 text-sm text-muted-foreground">
              {confirmation.confirmed_at ? 'Approved.' : 'Rejected.'}
            </p>
          ) : (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              {needsSecond && !acknowledged ? (
                <Button size="sm" variant="destructive" onClick={() => setAcknowledged(true)}>
                  I understand — this cannot be undone
                </Button>
              ) : (
                <Button
                  size="sm"
                  disabled={busy}
                  onClick={() => run(() => onConfirm(confirmation.tool_call_id, needsSecond))}
                >
                  <Check className="size-3.5" />
                  Approve
                </Button>
              )}
              <Button
                size="sm"
                variant="ghost"
                disabled={busy}
                onClick={() => run(() => onReject(confirmation.tool_call_id))}
              >
                <X className="size-3.5" />
                Reject
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
